import React, { useCallback, useMemo } from "react";
import { useWallet } from "@solana/wallet-adapter-react";
import { useWalletModal } from "@solana/wallet-adapter-react-ui";

/**
 * Minimal Solana wallet connect button, styled to match the fire palette.
 * Shows "Connect" when idle, the truncated pubkey when connected. No chain
 * icon — we're always on Solana.
 */
export default function BoomConnectButton() {
  const { connected, connecting, publicKey, disconnect } = useWallet();
  const { setVisible } = useWalletModal();

  const handleClick = useCallback(() => {
    if (connected) {
      // Wallet adapter persists `walletName` in localStorage and uses it
      // to autoConnect on next mount — meaning a tap of Disconnect would
      // immediately re-grab the same wallet (e.g. Solflare) and reconnect.
      // Clearing the stored name forces the next session to start at the
      // wallet picker, so the user can switch or stay disconnected.
      try { window.localStorage.removeItem("walletName"); } catch { /* noop */ }
      disconnect().catch(() => {});
    } else {
      setVisible(true);
    }
  }, [connected, disconnect, setVisible]);

  const label = useMemo(() => {
    if (connecting) return "Connecting…";
    if (!connected || !publicKey) return "Connect";
    const k = publicKey.toBase58();
    return `${k.slice(0, 4)}…${k.slice(-4)}`;
  }, [connected, connecting, publicKey]);

  return (
    <button
      type="button"
      className={`pm-wallet-btn ${connected ? "pm-wallet-btn--on" : ""}`}
      onClick={handleClick}
      title={connected ? "Disconnect wallet" : "Connect a Solana wallet"}
    >
      {connected && (
        <span className="pm-wallet-btn__dot" aria-hidden="true" />
      )}
      <span className="pm-wallet-btn__label">{label}</span>
    </button>
  );
}
